import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Input, InputNumber, Select, Button, Space, DatePicker, message } from 'antd';
import { PlusOutlined, DatabaseOutlined, DollarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { AccountsReceivableDashboard } from './AccountsReceivable';
import { apiFetch } from '../../utils/apiFetch';
import API_URL from '../../config/api';
import { fetchBills, addBill as apiAddBill, updateBill as apiUpdateBill, deleteBill as apiDeleteBill } from '../../utils/billApi';
import {
  parseAccountingPeriod,
  enrichLedgerEntry,
  buildVatReportFromLedgerAndInvoices,
  computeLedgerTotals,
  normalizeDateStr,
  type VatReportResult,
} from '../../utils/vatAccounting';
import { ListPagination, paginateList } from '../../components/ListPagination';
import '../../styles/listPage.css';

interface Transaction {
  id: string;
  type: 'income' | 'expense';
  category: string;
  amount: number;
  note?: string;
  date: string;
  source?: string;
  supplier_id?: string | null;
  supplier_name?: string | null;
  supplier_notes?: string | null;
  customer_name?: string | null;
}

interface TransactionSummary {
  income: number;
  expense: number;
  income_net: number;
  expense_net: number;
  netProfit: number;
  output_vat: number;
  input_vat: number;
  net_vat: number;
}

interface Bill {
  id: string;
  name: string;
  amount: number;
  frequency: 'weekly' | 'monthly' | 'quarterly' | 'annually';
  category: string;
  next_due: string;
  notes?: string;
  status: 'active' | 'paused';
}

function apBucketForDate(dateStr: string | undefined): 'current_amount' | 'b1_30' | 'b31_60' | 'b61_90' | 'b90_plus' {
  const due = new Date(String(dateStr || '').split('T')[0] + 'T00:00:00');
  if (Number.isNaN(due.getTime())) return 'current_amount';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysOverdue = Math.floor((today.getTime() - due.getTime()) / 86400000);
  if (daysOverdue <= 0) return 'current_amount';
  if (daysOverdue <= 30) return 'b1_30';
  if (daysOverdue <= 60) return 'b31_60';
  if (daysOverdue <= 90) return 'b61_90';
  return 'b90_plus';
}

const AP_OPEN_PO_STATUSES = new Set(['Received', 'Partial', 'Partially Received', 'Delivered']);

function buildApAgingFromPurchaseOrders(orders: any[], supplierList: any[]) {
  const supplierMap = new Map(
    supplierList.map(s => [String(s.id), s.name || s.supplier_name || 'Unknown Supplier'])
  );
  const bySupplier = new Map<string, any>();

  for (const po of orders) {
    if (!AP_OPEN_PO_STATUSES.has(po.status)) continue;
    const total = Number(po.total || 0);
    if (total <= 0) continue;
    const sid = String(po.supplier_id ?? po.supplierId ?? 'unknown');
    const bucket = apBucketForDate(po.expected_date || po.expectedDate || po.created_at);
    if (!bySupplier.has(sid)) {
      bySupplier.set(sid, {
        supplier_name: supplierMap.get(sid) || po.supplier_name || 'Unknown Supplier',
        supplier_id: po.supplier_id ?? po.supplierId,
        po_count: 0,
        current_amount: 0,
        b1_30: 0,
        b31_60: 0,
        b61_90: 0,
        b90_plus: 0,
        total_owed: 0,
      });
    }
    const row = bySupplier.get(sid);
    row.po_count += 1;
    row[bucket] += total;
    row.total_owed += total;
  }

  const report = Array.from(bySupplier.values()).sort((a, b) => b.total_owed - a.total_owed);
  const totals = report.reduce(
    (acc, row) => ({
      current: acc.current + row.current_amount,
      b1_30: acc.b1_30 + row.b1_30,
      b31_60: acc.b31_60 + row.b31_60,
      b61_90: acc.b61_90 + row.b61_90,
      b90_plus: acc.b90_plus + row.b90_plus,
      total: acc.total + row.total_owed,
    }),
    { current: 0, b1_30: 0, b31_60: 0, b61_90: 0, b90_plus: 0, total: 0 }
  );
  return { report, totals };
}

async function loadVatReport(period: string, token: string): Promise<VatReportResult> {
  const res = await apiFetch(`${API_URL}/accounting/vat-report?period=${period}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.ok) {
    const data = await res.json();
    if (data.output_vat != null && data.input_vat != null) return data;
  } else if (res.status !== 404) {
    throw new Error(`HTTP ${res.status}`);
  }

  const range = parseAccountingPeriod(period);
  const headers = { Authorization: `Bearer ${token}` };
  const [ledgerRes, invRes] = await Promise.all([
    apiFetch(`${API_URL}/accounting?period=${period}`, { headers }),
    apiFetch(`${API_URL}/invoices`, { headers }),
  ]);
  if (!ledgerRes.ok || !invRes.ok) throw new Error('Failed to load VAT fallback data');
  const ledgerData = await ledgerRes.json();
  const invoices = await invRes.json();
  const entries = (Array.isArray(ledgerData.entries) ? ledgerData.entries : []).map(enrichLedgerEntry);
  return buildVatReportFromLedgerAndInvoices(range, entries, Array.isArray(invoices) ? invoices : []);
}

async function loadApAging(token: string) {
  const res = await apiFetch(`${API_URL}/accounting/ap-aging`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.ok) {
    const data = await res.json();
    return { report: data.report || [], totals: data.totals };
  }
  if (res.status !== 404) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `HTTP ${res.status}`);
  }

  const headers = { Authorization: `Bearer ${token}` };
  const [poRes, supRes] = await Promise.all([
    apiFetch(`${API_URL}/purchase-orders`, { headers }),
    apiFetch(`${API_URL}/suppliers`, { headers }),
  ]);
  if (!poRes.ok) throw new Error(`HTTP ${poRes.status}`);
  const poData = await poRes.json();
  const orders = Array.isArray(poData) ? poData : (poData.orders || poData.rows || []);
  const suppliers = supRes.ok ? await supRes.json() : [];
  return buildApAgingFromPurchaseOrders(orders, Array.isArray(suppliers) ? suppliers : []);
}

const ACCOUNTING_TABS = ['summary', 'bills', 'receivable', 'vat', 'payable'] as const;
type AccountingTab = typeof ACCOUNTING_TABS[number];

function normalizeAccountingTab(tab: string | null): AccountingTab {
  if (tab && ACCOUNTING_TABS.includes(tab as AccountingTab)) {
    return tab as AccountingTab;
  }
  return 'summary';
}

const Accounting: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTabState] = useState<AccountingTab>(
    () => normalizeAccountingTab(searchParams.get('tab'))
  );
  const setActiveTab = (tab: AccountingTab) => {
    setActiveTabState(tab);
    if (tab === 'summary') setSearchParams({});
    else setSearchParams({ tab });
  };
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [viewTransaction, setViewTransaction] = useState<Transaction | null>(null);
  const [period, setPeriod] = useState('3m');
  const [summary, setSummary] = useState<TransactionSummary>({
    income: 0, expense: 0, income_net: 0, expense_net: 0, netProfit: 0,
    output_vat: 0, input_vat: 0, net_vat: 0,
  });
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');
  const [listPage, setListPage] = useState(1);
  const [error, setError] = useState<string>('');
  const [vatReport, setVatReport] = useState<VatReportResult | null>(null);
  const [vatLoading, setVatLoading] = useState(false);
  // AP Aging state
  const [apAging, setApAging] = useState<any[] | null>(null);
  const [apTotals, setApTotals] = useState<any>(null);
  const [apLoading, setApLoading] = useState(false);
  const [apError, setApError] = useState<string | null>(null);
  const [vatError, setVatError] = useState<string | null>(null);
  // Suppliers for the expense form
  const [suppliers, setSuppliers] = useState<{ id: string; name: string; notes?: string }[]>([]);
  // Bills stored in backend
  const [bills, setBills] = useState<Bill[]>([]);
    // Fetch bills from backend
    const fetchBillsFromApi = async () => {
      if (!token) return;
      try {
        const bills = await fetchBills(token);
        setBills(bills);
      } catch (err) {
        setError('Failed to load bills');
      }
    };

    useEffect(() => {
      if (token) fetchBillsFromApi();
      // eslint-disable-next-line
    }, [token]);

  // Fetch suppliers
  useEffect(() => {
    if (!token) return;
    apiFetch(`${API_URL}/suppliers`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => setSuppliers(Array.isArray(data) ? data.map((s: any) => ({ id: String(s.id || s._id), name: s.name, notes: s.notes })) : []))
      .catch(() => {});
  }, [token]);

  const [newBill, setNewBill] = useState<{ name: string; amount: number | undefined; frequency: Bill['frequency']; category: string; next_due: string; notes: string }>({ name: '', amount: undefined, frequency: 'monthly', category: 'Subscription', next_due: '', notes: '' });
  const [billError, setBillError] = useState('');
  const [billLoading, setBillLoading] = useState(false);
  const [success, setSuccess] = useState<string>('');

  // Form state
  const [newTransaction, setNewTransaction] = useState({
    type: 'expense' as 'income' | 'expense',
    category: '',
    amount: '',
    note: '',
    date: new Date().toISOString().split('T')[0],
    supplier_id: '' as string
  });

  // Fetch transactions
  const fetchTransactions = React.useCallback(async () => {
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const url = `${API_URL}/accounting?period=${period}${selectedCategory ? `&category=${selectedCategory}` : ''}`;

      const response = await apiFetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          navigate('/login');
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();

      // Defensive: map both camelCase and snake_case fields for transactions
      try {
        const entries = (Array.isArray(data.entries) ? data.entries : []).map((t: any) => {
          if (!t || typeof t !== 'object') {
            console.warn('⚠️ Invalid transaction entry:', t);
            return null;
          }
          return {
            id: t.id || `temp-${Date.now()}-${Math.random()}`,
            type: t.type || 'income',
            category: t.category || 'Uncategorized',
            amount: parseFloat(t.amount_gross ?? t.amount ?? t.amount_net ?? t.total ?? 0) || 0,
            amount_gross: parseFloat(t.amount_gross ?? t.amount ?? 0) || 0,
            amount_net: parseFloat(t.amount_net ?? t.amount ?? 0) || 0,
            vat_amount: parseFloat(t.vat_amount ?? 0) || 0,
            note: t.note ?? t.notes ?? '',
            date: normalizeDateStr(t.date || t.created_at || t.createdAt) || new Date().toISOString().split('T')[0],
            source: t.source ?? undefined,
            supplier_id: t.supplier_id ?? null,
            supplier_name: t.supplier_name ?? null,
            supplier_notes: t.supplier_notes ?? null,
            customer_name: t.customer_name ?? null,
          };
        }).filter(Boolean);
        
        setTransactions(entries);
        setCategories(Array.isArray(data.categories) ? data.categories : []);

        let apiTotals = data.totals || {};
        if (apiTotals.income_net == null && Array.isArray(data.entries)) {
          const enriched = data.entries.map((t: any) => enrichLedgerEntry(t));
          const ledgerTotals = computeLedgerTotals(enriched);
          apiTotals = {
            ...apiTotals,
            income_net: ledgerTotals.income_net,
            expense_net: ledgerTotals.expense_net,
            netProfit: ledgerTotals.netProfit,
          };
        }
        if (apiTotals.output_vat == null) {
          try {
            const vat = await loadVatReport(period, token);
            apiTotals = {
              ...apiTotals,
              output_vat: vat.output_vat,
              input_vat: vat.input_vat,
              net_vat: vat.net_vat,
            };
          } catch {
            // VAT totals remain 0 until backend/fallback loads
          }
        }
        setSummary({
          income: parseFloat(apiTotals.income ?? 0) || 0,
          expense: parseFloat(apiTotals.expense ?? 0) || 0,
          income_net: parseFloat(apiTotals.income_net ?? apiTotals.income ?? 0) || 0,
          expense_net: parseFloat(apiTotals.expense_net ?? apiTotals.expense ?? 0) || 0,
          netProfit: parseFloat(apiTotals.netProfit ?? 0) || 0,
          output_vat: parseFloat(apiTotals.output_vat ?? 0) || 0,
          input_vat: parseFloat(apiTotals.input_vat ?? 0) || 0,
          net_vat: parseFloat(apiTotals.net_vat ?? 0) || 0,
        });
      } catch (parseError) {
        console.error('❌ Error parsing accounting data:', parseError);
        setError('Failed to parse accounting data');
        setTransactions([]);
        setCategories([]);
      }

    } catch (error: any) {
      console.error('Failed to fetch transactions:', error);
      setError(error.message || 'Failed to load transactions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token, period, selectedCategory, navigate]);

  const fetchVatReport = React.useCallback(async () => {
    if (!token) return;
    setVatLoading(true);
    setVatError(null);
    try {
      const report = await loadVatReport(period, token);
      setVatReport(report);
    } catch (err: any) {
      setVatError(err.message || 'Failed to load VAT report');
      setVatReport(null);
    } finally {
      setVatLoading(false);
    }
  }, [token, period]);

  useEffect(() => {
    if (token) {
      fetchTransactions();
    }
  }, [token, fetchTransactions]);

  useEffect(() => {
    if (token && (activeTab === 'vat' || activeTab === 'summary')) {
      fetchVatReport();
    }
  }, [token, period, activeTab, fetchVatReport]);

  useEffect(() => {
    const raw = searchParams.get('tab');
    const tab = normalizeAccountingTab(raw);
    if (raw && tab === 'summary' && raw !== 'summary') {
      setSearchParams({});
    }
    setActiveTabState(tab);
  }, [searchParams, setSearchParams]);

  // Bill helpers
  const markBillPaid = async (bill: Bill) => {
    try {
      await apiFetch(`${API_URL}/accounting`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          type: 'expense',
          category: bill.category,
          amount: bill.amount,
          note: `${bill.name} — ${bill.frequency} bill`,
          date: new Date().toISOString().split('T')[0]
        })
      });
      // Advance next_due by frequency
      const d = new Date(bill.next_due || new Date());
      if (bill.frequency === 'weekly') d.setDate(d.getDate() + 7);
      else if (bill.frequency === 'monthly') d.setMonth(d.getMonth() + 1);
      else if (bill.frequency === 'quarterly') d.setMonth(d.getMonth() + 3);
      else d.setFullYear(d.getFullYear() + 1);
      const updatedBill = { ...bill, next_due: d.toISOString().split('T')[0] };
      await apiUpdateBill(bill.id, updatedBill, token);
      await fetchBillsFromApi();
      message.success(`${bill.name} marked as paid — expense added to ledger`);
      fetchTransactions();
    } catch {
      message.error('Failed to record payment');
    }
  };

  const addBill = async () => {
    setBillError('');
    if (!newBill.name.trim()) { setBillError('Bill name is required'); return; }
    if (newBill.amount === undefined || newBill.amount === null || isNaN(Number(newBill.amount)) || Number(newBill.amount) <= 0) { setBillError('A valid amount greater than 0 is required'); return; }
    if (!token) { setBillError('You are not logged in'); return; }
    const bill = {
      name: newBill.name.trim(),
      amount: Number(newBill.amount),
      frequency: newBill.frequency,
      category: newBill.category,
      next_due: newBill.next_due || new Date().toISOString().split('T')[0],
      notes: newBill.notes,
      status: 'active'
    };
    try {
      setBillLoading(true);
      await apiAddBill(bill, token);
      await fetchBillsFromApi();
      setNewBill({ name: '', amount: undefined, frequency: 'monthly', category: 'Subscription', next_due: '', notes: '' });
      message.success('Bill added successfully');
    } catch (err: any) {
      setBillError('Failed to add bill: ' + (err?.message || 'Unknown error'));
    } finally {
      setBillLoading(false);
    }
  };

  const deleteBill = async (id: string) => {
    try {
      await apiDeleteBill(id, token);
      await fetchBillsFromApi();
      message.success('Bill deleted');
    } catch {
      message.error('Failed to delete bill');
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    if (!newTransaction.category.trim()) {
      setError('Category is required');
      return;
    }

    const amount = parseFloat(newTransaction.amount);
    if (!newTransaction.amount || isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount greater than 0');
      return;
    }

    if (!token) {
      navigate('/login');
      return;
    }

    try {
      setSubmitting(true);

      const response = await apiFetch(`${API_URL}/accounting`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          type: newTransaction.type,
          category: newTransaction.category.trim(),
          amount: amount,
          note: newTransaction.note.trim(),
          date: newTransaction.date,
          supplier_id: newTransaction.supplier_id || undefined
        })
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || data.error || `HTTP ${response.status}`);
      }

      setSuccess('✅ Expense added successfully!');
      
      setNewTransaction({
        type: 'expense',
        category: '',
        amount: '',
        note: '',
        date: new Date().toISOString().split('T')[0],
        supplier_id: ''
      });
      
      await fetchTransactions();

      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error: any) {
      console.error('Failed to create transaction:', error);
      setError(error.message || 'Failed to create transaction. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Export CSV utility
  const exportCSV = () => {
    if (!transactions.length) {
      message.info('No transactions to export.');
      return;
    }
    const headers = ['Date', 'Type', 'Category', 'Customer / Name', 'Amount', 'Note', 'Supplier', 'Entry ID'];
    const rows = transactions.map(t => [
      new Date(t.date).toLocaleDateString('en-GB'),
      t.type,
      t.category,
      t.customer_name || t.supplier_name || '',
      t.amount,
      t.note ? '"' + String(t.note).replace(/"/g, '""') + '"' : '',
      t.supplier_name || '',
      t.id
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Client-side filtered list (applied after server data is loaded)
  const filteredTransactions = transactions.filter(t => {
    if (selectedType && t.type !== selectedType) return false;
    if (searchText) {
      const s = searchText.toLowerCase();
      const match = [t.customer_name, t.note, t.category, t.supplier_name]
        .some(v => v && v.toLowerCase().includes(s));
      if (!match) return false;
    }
    return true;
  });

  const { pages: txPages, safePage: safeListPage, slice: paginatedTransactions } =
    paginateList(filteredTransactions, listPage);

  useEffect(() => {
    setListPage(1);
  }, [searchText, selectedType, transactions.length]);

  // Handle delete
  const handleDelete = async (id: string) => {
    const tx = transactions.find(t => t.id === id);
    if (tx && (tx as any).source === 'payment') {
      message.warning('Invoice payment entries cannot be deleted.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this transaction?')) {
      return;
    }
    if (!token) {
      navigate('/login');
      return;
    }
    try {
      setDeleting(id);
      setError('');
      const response = await apiFetch(`${API_URL}/accounting/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || `HTTP ${response.status}`);
      }
      setSuccess('🗑️ Transaction deleted successfully!');
      await fetchTransactions();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error: any) {
      console.error('Failed to delete transaction:', error);
      setError(error.message || 'Failed to delete transaction. Please try again.');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div style={styles.container}>

      {/* ── Transaction Detail Modal ── */}
      {viewTransaction && (
        <div
          onClick={() => setViewTransaction(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 12, padding: 32, width: 480, maxWidth: '95vw',
              boxShadow: '0 8px 40px rgba(0,0,0,0.18)'
            }}
          >
            {/* Modal header strip */}
            <div style={{
              background: viewTransaction.type === 'income' ? '#2563eb' : '#dc2626',
              borderRadius: 8, padding: '14px 20px', marginBottom: 24,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between'
            }}>
              <div>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 18 }}>
                  {viewTransaction.type === 'income' ? '💰 Income' : '💸 Expense'}
                </div>
                <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, marginTop: 2 }}>
                  {new Date(viewTransaction.date).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </div>
              </div>
              <div style={{ color: '#fff', fontWeight: 800, fontSize: 26 }}>
                £{Number(viewTransaction.amount).toFixed(2)}
              </div>
            </div>

            {/* Fields */}
            {([
              ['Type',     viewTransaction.type === 'income' ? 'Income' : 'Expense'],
              ['Category', viewTransaction.category],
              ['Amount',   `£${Number(viewTransaction.amount).toFixed(2)}`],
              ['Date',     new Date(viewTransaction.date).toLocaleDateString('en-GB')],
              ['Note',     viewTransaction.note || '—'],
              ...(viewTransaction.supplier_name ? [['Supplier', viewTransaction.supplier_name] as [string, string]] : []),
              ...(viewTransaction.supplier_notes ? [['Supplier Notes', viewTransaction.supplier_notes] as [string, string]] : []),
              ['Entry ID', viewTransaction.id],
            ] as [string, string][]).map(([label, value]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
                <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
                <span style={{ fontSize: 14, color: '#111827', fontWeight: 500, maxWidth: '60%', textAlign: 'right', wordBreak: 'break-word' }}>{value}</span>
              </div>
            ))}

            <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setViewTransaction(null)}
                style={{ padding: '9px 22px', border: '1px solid #d1d5db', borderRadius: 7, background: '#fff', color: '#374151', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
              >
                Close
              </button>

            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 24, margin: 0 }}>📕 Accounting</h2>
        <p style={{ color: '#666' }}>Manage your financial transactions and receivables</p>
      </div>

      {/* Primary Tab Switcher */}
      <div style={{
        display: 'flex',
        gap: 0,
        marginBottom: 20,
        borderBottom: '1px solid #d1d5db',
        overflowX: 'auto',
        flexWrap: 'wrap',
        WebkitOverflowScrolling: 'touch',
      }}>
        <button
          onClick={() => setActiveTab('summary')}
          style={{
            padding: '12px 24px',
            border: 'none',
            borderBottom: activeTab === 'summary' ? '3px solid #2563eb' : '3px solid transparent',
            background: 'none',
            fontSize: 15,
            fontWeight: 600,
            cursor: 'pointer',
            color: activeTab === 'summary' ? '#2563eb' : '#6b7280',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          <DatabaseOutlined /> General Ledger
          {transactions.length > 0 ? (
            <span style={{ fontSize: 11, background: '#dbeafe', color: '#2563eb', padding: '2px 8px', borderRadius: 10 }}>
              {filteredTransactions.length}
            </span>
          ) : null}
        </button>
        <button
          onClick={() => setActiveTab('bills')}
          style={{
            padding: '12px 24px',
            border: 'none',
            borderBottom: activeTab === 'bills' ? '3px solid #d97706' : '3px solid transparent',
            background: 'none',
            fontSize: 15,
            fontWeight: 600,
            cursor: 'pointer',
            color: activeTab === 'bills' ? '#d97706' : '#6b7280',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          🧾 Bills & Subscriptions
        </button>
        <button
          onClick={() => setActiveTab('receivable')}
          style={{
            padding: '12px 24px',
            border: 'none',
            borderBottom: activeTab === 'receivable' ? '3px solid #16a34a' : '3px solid transparent',
            background: 'none',
            fontSize: 15,
            fontWeight: 600,
            cursor: 'pointer',
            color: activeTab === 'receivable' ? '#16a34a' : '#6b7280',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          <DollarOutlined /> 📡 Radar AR
        </button>
        <button
          onClick={() => setActiveTab('vat')}
          style={{
            padding: '12px 24px',
            border: 'none',
            borderBottom: activeTab === 'vat' ? '3px solid #7c3aed' : '3px solid transparent',
            background: 'none',
            fontSize: 15,
            fontWeight: 600,
            cursor: 'pointer',
            color: activeTab === 'vat' ? '#7c3aed' : '#6b7280',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          🇬🇧 VAT Report
        </button>
        <button
          onClick={() => {
            setActiveTab('payable');
            if (!apAging) {
              setApLoading(true);
              setApError(null);
              loadApAging(token!)
                .then(d => { setApAging(d.report || []); setApTotals(d.totals); })
                .catch((err) => { setApError(err.message || 'Failed to load AP aging'); setApAging([]); setApTotals(null); })
                .finally(() => setApLoading(false));
            }
          }}
          style={{
            padding: '12px 24px',
            border: 'none',
            borderBottom: activeTab === 'payable' ? '3px solid #dc2626' : '3px solid transparent',
            background: 'none',
            fontSize: 15,
            fontWeight: 600,
            cursor: 'pointer',
            color: activeTab === 'payable' ? '#dc2626' : '#6b7280',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          🔴 AP Aging
        </button>
      </div>

      {activeTab === 'summary' ? (
        <>
          {error && (
            <div style={styles.error}>
              ❌ {error}
            </div>
          )}
          
          {success && (
            <div style={styles.success}>
              {success}
            </div>
          )}

          <Card>
            {/* Filters & Export */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
              <Select value={period} onChange={setPeriod} style={{ width: 150 }}>
                <Select.Option value="3m">Last 3 Months</Select.Option>
                <Select.Option value="6m">Last 6 Months</Select.Option>
                <Select.Option value="9m">Last 9 Months</Select.Option>
                <Select.Option value="1y">Last Year</Select.Option>
                <Select.Option value="all">All Time</Select.Option>
              </Select>
              <Select
                value={selectedCategory}
                onChange={setSelectedCategory}
                style={{ width: 180 }}
                placeholder="All Categories"
              >
                <Select.Option value="">All Categories</Select.Option>
                {categories.map(category => (
                  <Select.Option key={category} value={category}>{category}</Select.Option>
                ))}
              </Select>
              <Select
                value={selectedType}
                onChange={setSelectedType}
                style={{ width: 150 }}
                placeholder="All Types"
              >
                <Select.Option value="">💼 All Types</Select.Option>
                <Select.Option value="income">💰 Income</Select.Option>
                <Select.Option value="expense">💸 Expense</Select.Option>
              </Select>
              <Input
                placeholder="🔍 Search customer, note, category..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                allowClear
                style={{ width: 260 }}
              />
              <Button
                onClick={() => {
                  setSelectedCategory('');
                  setSelectedType('');
                  setSearchText('');
                }}
                type="default"
              >
                Clear filters
              </Button>
              <Button onClick={exportCSV} icon={<DatabaseOutlined />} type="default" style={{ fontWeight: 600, marginLeft: 'auto' }}>
                Export CSV
              </Button>
            </div>

            {/* Summary Banner — ex-VAT figures aligned with VAT report period */}
            <div style={styles.summaryBanner}>
              <div>
                <strong>Income (ex VAT):</strong> £{summary.income_net.toFixed(2)}
              </div>
              <div>
                <strong>Expenses (ex VAT):</strong> £{summary.expense_net.toFixed(2)}
              </div>
              <div>
                <strong>Net Profit:</strong> £{summary.netProfit.toFixed(2)}
              </div>
              <div>
                <strong>Output VAT:</strong> £{summary.output_vat.toFixed(2)}
              </div>
              <div>
                <strong>Input VAT:</strong> £{summary.input_vat.toFixed(2)}
              </div>
              <div>
                <strong>Net VAT:</strong> £{summary.net_vat.toFixed(2)}
              </div>
            </div>

            {/* Info banner: income auto-posts */}
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: 13, color: '#1e40af' }}>
              💡 <strong>Income (ex VAT)</strong> is derived from invoice payments. <strong>Output VAT</strong> comes from invoice issue dates; <strong>Input VAT</strong> from expense entries (PO receipts and manual expenses). All figures use the selected period above.
            </div>

            {/* Add Expense Form */}
            <div style={styles.formSection}>
              <h2 style={{margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600}}>➕ Add Expense</h2>
              <div style={styles.form}>
                <div>
                  <label style={styles.label}>Category *</label>
                  <Select
                    value={newTransaction.category}
                    onChange={(value) => setNewTransaction(prev => ({ ...prev, type: 'expense', category: value }))}
                    style={{ width: '100%' }}
                    placeholder="Select category"
                    disabled={submitting}
                  >
                    <Select.Option value="Rent">🏢 Rent</Select.Option>
                    <Select.Option value="Utilities">💡 Utilities</Select.Option>
                    <Select.Option value="Payroll">👥 Payroll</Select.Option>
                    <Select.Option value="Software & Subscriptions">💻 Software &amp; Subscriptions</Select.Option>
                    <Select.Option value="Marketing">📣 Marketing</Select.Option>
                    <Select.Option value="Travel & Transport">🚗 Travel &amp; Transport</Select.Option>
                    <Select.Option value="Office Supplies">📦 Office Supplies</Select.Option>
                    <Select.Option value="Professional Fees">💼 Professional Fees</Select.Option>
                    <Select.Option value="Insurance">🛡️ Insurance</Select.Option>
                    <Select.Option value="Bank Charges">🏦 Bank Charges</Select.Option>
                    <Select.Option value="Stock &amp; Inventory">📳 Stock &amp; Inventory</Select.Option>
                    <Select.Option value="Equipment">🔧 Equipment</Select.Option>
                    <Select.Option value="Other Expense">💸 Other Expense</Select.Option>
                  </Select>
                </div>

                <div>
                  <label style={styles.label}>Amount (inc VAT) *</label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={newTransaction.amount}
                    onChange={(e) => setNewTransaction(prev => ({ ...prev, amount: e.target.value }))}
                    step="0.01"
                    min="0"
                    disabled={submitting}
                  />
                </div>

                <div>
                  <label style={styles.label}>Date *</label>
                  <Input
                    type="date"
                    value={newTransaction.date}
                    onChange={(e) => setNewTransaction(prev => ({ ...prev, date: e.target.value }))}
                    disabled={submitting}
                  />
                </div>

                <div>
                  <label style={styles.label}>Supplier (optional)</label>
                  <Select
                    value={newTransaction.supplier_id || undefined}
                    onChange={(v) => setNewTransaction(prev => ({ ...prev, supplier_id: v || '' }))}
                    style={{ width: '100%' }}
                    placeholder="Link to supplier"
                    allowClear
                    disabled={submitting}
                  >
                    {suppliers.map(s => (
                      <Select.Option key={s.id} value={s.id}>{s.name}</Select.Option>
                    ))}
                  </Select>
                </div>

                <div style={{gridColumn: '1 / -1'}}>
                  <label style={styles.label}>Note (optional)</label>
                  <Input
                    placeholder="Add a note..."
                    value={newTransaction.note}
                    onChange={(e) => setNewTransaction(prev => ({ ...prev, note: e.target.value }))}
                    disabled={submitting}
                  />
                </div>

                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleSubmit}
                  loading={submitting}
                  style={{gridColumn: '1 / -1'}}
                >
                  Add Expense
                </Button>
              </div>
            </div>

            {/* Transactions Table */}
            <ListPagination
              total={transactions.length}
              filtered={filteredTransactions.length}
              page={safeListPage}
              pages={txPages}
              onPageChange={setListPage}
              noun="transactions"
            />
            <div className="list-table-panel">
            <div style={styles.tableWrapper}>
              {loading ? (
                <p style={{textAlign: 'center', padding: '40px', color: '#6b7280'}}>Loading transactions...</p>
              ) : filteredTransactions.length > 0 ? (
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.headerRow}>
                      <th style={styles.th}>Date</th>
                      <th style={styles.th}>Type</th>
                      <th style={styles.th}>Category</th>
                      <th style={styles.th}>Customer / Name</th>
                      <th style={styles.th}>Amount (inc VAT)</th>
                      <th style={styles.th}>Note</th>
                      <th style={styles.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTransactions.map((transaction) => (
                      <tr 
                        key={transaction.id} 
                        style={styles.row}
                      >
                        <td style={styles.td}>
                          {new Date(transaction.date).toLocaleDateString('en-GB')}
                        </td>
                        <td style={styles.td}>
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: 600,
                            background: transaction.type === 'income' ? '#dbeafe' : '#fee2e2',
                            color: transaction.type === 'income' ? '#2563eb' : '#dc2626'
                          }}>
                            {transaction.type === 'income' ? '💰' : '💸'} {transaction.type}
                          </span>
                        </td>
                        <td style={styles.td}>{transaction.category}</td>
                        <td style={{ ...styles.td, fontWeight: 600, color: '#374151' }}>
                          {transaction.customer_name || transaction.supplier_name || '—'}
                        </td>
                        <td style={{ 
                          ...styles.td,
                          fontWeight: 700,
                          color: transaction.type === 'income' ? '#16a34a' : '#dc2626'
                        }}>
                          £{Number(transaction.amount || 0).toFixed(2)}
                        </td>
                        <td style={styles.td}>{transaction.note || '—'}</td>
                        <td style={styles.td}>
                          <button
                            onClick={() => setViewTransaction(transaction)}
                            style={{
                              padding: '5px 14px', border: '1px solid #d1d5db',
                              borderRadius: 6, background: '#fff', color: '#374151',
                              fontWeight: 600, fontSize: 12, cursor: 'pointer'
                            }}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p style={{textAlign: 'center', padding: '40px', color: '#6b7280'}}>
                  {transactions.length === 0
                    ? 'No transactions in this period. Try “All Time” or add an expense above.'
                    : 'No transactions match your filters. Click Clear filters or widen the date range.'}
                </p>
              )}
            </div>
            </div>

            <div className="list-mobile-cards">
              {paginatedTransactions.map(transaction => (
                <div key={transaction.id} className="list-mobile-card">
                  <div className="list-mobile-card-header">
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="list-mobile-card-title">{transaction.category}</div>
                      <div className="list-mobile-card-meta">{transaction.customer_name || transaction.supplier_name || '—'}</div>
                    </div>
                    <span style={{
                      padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                      background: transaction.type === 'income' ? '#dbeafe' : '#fee2e2',
                      color: transaction.type === 'income' ? '#2563eb' : '#dc2626',
                    }}>
                      £{Number(transaction.amount || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="list-mobile-card-info">
                    <div><div className="list-mobile-card-label">Date</div>{new Date(transaction.date).toLocaleDateString('en-GB')}</div>
                    <div><div className="list-mobile-card-label">Type</div>{transaction.type}</div>
                    <div style={{ gridColumn: '1 / -1' }}><div className="list-mobile-card-label">Note</div>{transaction.note || '—'}</div>
                  </div>
                  <div className="list-mobile-actions">
                    <button
                      onClick={() => setViewTransaction(transaction)}
                      style={{ padding: '5px 14px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', color: '#374151', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
                    >
                      View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </>
      ) : activeTab === 'bills' ? (
        <Card>
          <h3 style={{ margin: '0 0 6px 0', fontWeight: 700, fontSize: 18 }}>🧾 Bills &amp; Subscriptions</h3>
          <p style={{ color: '#6b7280', marginBottom: 20 }}>Track recurring payments. Clicking <strong>Pay Now</strong> automatically adds the expense to your General Ledger.</p>

          {/* Add Bill Form */}
          <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: 20, marginBottom: 24 }}>
            <h4 style={{ margin: '0 0 14px 0', fontWeight: 600 }}>Add New Bill</h4>
            {billError && <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontWeight: 600 }}>❌ {billError}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
              <div>
                <label style={styles.label}>Bill Name *</label>
                <Input placeholder="e.g. Netflix, Office 365" value={newBill.name} onChange={e => setNewBill(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div>
                <label style={styles.label}>Category</label>
                <Select value={newBill.category} onChange={v => setNewBill(p => ({ ...p, category: v }))} style={{ width: '100%' }}>
                  <Select.Option value="Subscription">Subscription</Select.Option>
                  <Select.Option value="Utilities">Utilities</Select.Option>
                  <Select.Option value="Rent">Rent</Select.Option>
                  <Select.Option value="Insurance">Insurance</Select.Option>
                  <Select.Option value="Software">Software</Select.Option>
                  <Select.Option value="Marketing">Marketing</Select.Option>
                  <Select.Option value="Other">Other</Select.Option>
                </Select>
              </div>
              <div>
                <label style={styles.label}>Amount (£) *</label>
                <InputNumber placeholder="0.00" value={newBill.amount} onChange={v => setNewBill(p => ({ ...p, amount: v ?? undefined }))} min={0.01} step={0.01} precision={2} style={{ width: '100%' }} />
              </div>
              <div>
                <label style={styles.label}>Frequency</label>
                <Select value={newBill.frequency} onChange={v => setNewBill(p => ({ ...p, frequency: v as Bill['frequency'] }))} style={{ width: '100%' }}>
                  <Select.Option value="weekly">Weekly</Select.Option>
                  <Select.Option value="monthly">Monthly</Select.Option>
                  <Select.Option value="quarterly">Quarterly</Select.Option>
                  <Select.Option value="annually">Annually</Select.Option>
                </Select>
              </div>
              <div>
                <label style={styles.label}>Next Due Date</label>
                <Input type="date" value={newBill.next_due} onChange={e => setNewBill(p => ({ ...p, next_due: e.target.value }))} />
              </div>
              <div>
                <label style={styles.label}>Notes</label>
                <Input placeholder="Optional..." value={newBill.notes} onChange={e => setNewBill(p => ({ ...p, notes: e.target.value }))} />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={addBill} loading={billLoading} style={{ width: '100%' }}>Add Bill</Button>
              </div>
            </div>
          </div>

          {/* Bills Table */}
          {bills.length === 0 ? (
            <p style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>No bills added yet. Add your first recurring bill above.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.headerRow}>
                    <th style={styles.th}>Bill Name</th>
                    <th style={styles.th}>Category</th>
                    <th style={styles.th}>Amount</th>
                    <th style={styles.th}>Frequency</th>
                    <th style={styles.th}>Next Due</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bills.map(bill => {
                    const isOverdue = bill.next_due && new Date(bill.next_due) < new Date(new Date().toDateString());
                    return (
                      <tr key={bill.id} style={styles.row}>
                        <td style={{ ...styles.td, fontWeight: 600 }}>{bill.name}</td>
                        <td style={styles.td}>{bill.category}</td>
                        <td style={{ ...styles.td, fontWeight: 700, color: '#dc2626' }}>£{Number(bill.amount).toFixed(2)}</td>
                        <td style={styles.td}><span style={{ textTransform: 'capitalize' }}>{bill.frequency}</span></td>
                        <td style={{ ...styles.td, color: isOverdue ? '#dc2626' : '#374151', fontWeight: isOverdue ? 700 : 400 }}>
                          {bill.next_due ? new Date(bill.next_due).toLocaleDateString('en-GB') : '—'}
                          {isOverdue && <span style={{ marginLeft: 6, fontSize: 11, background: '#fee2e2', color: '#dc2626', padding: '2px 6px', borderRadius: 4 }}>OVERDUE</span>}
                        </td>
                        <td style={styles.td}>
                          <span style={{ padding: '4px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: bill.status === 'active' ? '#dcfce7' : '#f3f4f6', color: bill.status === 'active' ? '#16a34a' : '#6b7280' }}>
                            {bill.status}
                          </span>
                        </td>
                        <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                          <button onClick={() => markBillPaid(bill)} style={{ padding: '5px 12px', border: 'none', borderRadius: 6, background: '#2563eb', color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                            ✅ Pay Now
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ) : activeTab === 'vat' ? (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h3 style={{ margin: 0, fontWeight: 700, fontSize: 18 }}>🇬🇧 VAT Summary Report</h3>
              <p style={{ color: '#6b7280', margin: '4px 0 0 0', fontSize: 14 }}>
                Output VAT (sales invoices) minus Input VAT (expenses & PO receipts). Same period as Summary: <strong>{period === '1y' ? 'Last 12 months' : period === 'all' ? 'All time' : `Last ${period.replace('m', ' months')}`}</strong>
                {vatReport?.date_from && vatReport?.date_to ? ` (${vatReport.date_from} → ${vatReport.date_to})` : ''}.
              </p>
            </div>
            <Select value={period} onChange={(v) => setPeriod(v)} style={{ width: 160 }}>
              <Select.Option value="3m">Last 3 Months</Select.Option>
              <Select.Option value="6m">Last 6 Months</Select.Option>
              <Select.Option value="9m">Last 9 Months</Select.Option>
              <Select.Option value="1y">Last Year</Select.Option>
              <Select.Option value="all">All Time</Select.Option>
            </Select>
          </div>

          {vatLoading ? (
            <p style={{ textAlign: 'center', color: '#6b7280', padding: 40 }}>Loading VAT data...</p>
          ) : vatError ? (
            <p style={{ textAlign: 'center', color: '#dc2626', padding: 40 }}>❌ {vatError}</p>
          ) : vatReport ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
                {[
                  { label: 'Output VAT', value: vatReport.output_vat, color: '#7c3aed' },
                  { label: 'Input VAT', value: vatReport.input_vat, color: '#059669' },
                  { label: 'Net VAT Due', value: vatReport.net_vat, color: vatReport.net_vat >= 0 ? '#dc2626' : '#2563eb' },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ background: '#fff', border: `2px solid ${color}33`, borderRadius: 10, padding: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>{label}</div>
                    <div style={{ fontSize: 28, fontWeight: 800, color, marginTop: 6 }}>£{Number(value).toFixed(2)}</div>
                  </div>
                ))}
              </div>

              {(vatReport.quarters || []).length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 }}>
                  {vatReport.quarters.map((q: any) => (
                    <div key={q.quarter} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8 }}>{q.label}</div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>Output: <strong style={{ color: '#7c3aed' }}>£{Number(q.output_vat).toFixed(2)}</strong></div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 2 }}>Input: <strong style={{ color: '#059669' }}>£{Number(q.input_vat).toFixed(2)}</strong></div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: '#111827', marginTop: 6 }}>Net: £{Number(q.net_vat).toFixed(2)}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>{q.invoice_count} invoice{q.invoice_count !== 1 ? 's' : ''} · {q.expense_count} expense{q.expense_count !== 1 ? 's' : ''}</div>
                    </div>
                  ))}
                </div>
              ) : vatReport.output_vat !== 0 || vatReport.input_vat !== 0 ? (
                <p style={{ textAlign: 'center', color: '#6b7280', padding: '20px 0' }}>
                  VAT totals are shown above for {vatReport.date_from} → {vatReport.date_to}. No quarterly breakdown for this range.
                </p>
              ) : (
                <p style={{ textAlign: 'center', color: '#9ca3af', padding: '20px 0' }}>No VAT activity in this period.</p>
              )}

              <div style={{ background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 8, padding: 14 }}>
                <p style={{ margin: 0, fontSize: 13, color: '#92400e' }}>
                  ⚠️ <strong>MTD Reminder:</strong> Net VAT = Output VAT − Input VAT. Submit your return via HMRC Making Tax Digital. Manual expenses assume 20% VAT inclusive unless stored separately.
                </p>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <p style={{ color: '#6b7280', marginBottom: 12 }}>VAT report not loaded yet.</p>
              <Button type="primary" onClick={fetchVatReport} loading={vatLoading}>
                Load VAT Report
              </Button>
            </div>
          )}
        </Card>
      ) : activeTab === 'receivable' ? (
        <AccountsReceivableDashboard />
      ) : activeTab === 'payable' ? (
        <Card style={{ borderRadius: 12 }}>
          <h3 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 700, color: '#111827' }}>🔴 Accounts Payable Aging</h3>
          <p style={{ margin: '0 0 20px', color: '#6b7280', fontSize: 13 }}>
            Outstanding balances owed to suppliers from received purchase orders, bucketed by age.
          </p>
          {apLoading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>⏳ Loading AP Aging…</div>
          ) : apError ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#dc2626' }}>❌ {apError}</div>
          ) : !apAging ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Click "AP Aging" tab above to load.</div>
          ) : apAging.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>✅</div>
              <div style={{ fontWeight: 600 }}>No outstanding payables</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>All received purchase orders appear to be settled.</div>
            </div>
          ) : (
            <>
              {/* Totals Banner */}
              {apTotals && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 12, marginBottom: 20 }}>
                  {[
                    { label: 'Current', value: apTotals.current, color: '#10b981' },
                    { label: '1–30 Days', value: apTotals.b1_30, color: '#f59e0b' },
                    { label: '31–60 Days', value: apTotals.b31_60, color: '#ea580c' },
                    { label: '61–90 Days', value: apTotals.b61_90, color: '#dc2626' },
                    { label: '90+ Days', value: apTotals.b90_plus, color: '#7f1d1d' },
                    { label: 'Total Owed', value: apTotals.total, color: '#1e3a8a' },
                  ].map(({ label, value, color }) => (
                    <div key={label} style={{ background: '#fff', border: `2px solid ${color}22`, borderRadius: 10, padding: '12px 14px' }}>
                      <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color }}>£{Number(value).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</div>
                    </div>
                  ))}
                </div>
              )}
              {/* Table */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#374151' }}>
                      {['Supplier', 'POs', 'Current', '1–30 Days', '31–60 Days', '61–90 Days', '90+ Days', 'Total'].map(h => (
                        <th key={h} style={{ padding: '10px 12px', textAlign: h === 'Supplier' ? 'left' : 'right', color: '#fff', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {apAging.map((row: any, i: number) => {
                      const overdue = (row.b1_30 + row.b31_60 + row.b61_90 + row.b90_plus) > 0;
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                          <td style={{ padding: '11px 12px', fontWeight: 700, color: '#111827' }}>
                            {row.supplier_name}
                            {overdue && <span style={{ marginLeft: 8, fontSize: 10, background: '#fef2f2', color: '#dc2626', padding: '1px 7px', borderRadius: 10, fontWeight: 700 }}>OVERDUE</span>}
                          </td>
                          <td style={{ padding: '11px 12px', textAlign: 'right', color: '#6b7280' }}>{row.po_count}</td>
                          {[row.current_amount, row.b1_30, row.b31_60, row.b61_90, row.b90_plus].map((v: number, j: number) => (
                            <td key={j} style={{ padding: '11px 12px', textAlign: 'right', color: v > 0 ? ['#10b981','#f59e0b','#ea580c','#dc2626','#7f1d1d'][j] : '#9ca3af', fontWeight: v > 0 ? 700 : 400 }}>
                              {v > 0 ? `£${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2 })}` : '—'}
                            </td>
                          ))}
                          <td style={{ padding: '11px 12px', textAlign: 'right', fontWeight: 800, color: '#1e3a8a' }}>
                            £{Number(row.total_owed).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>
      ) : null}
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    maxWidth: '1400px',
    margin: '0 auto',
    background: '#f3f4f6',
    minHeight: '100vh'
  },
  error: {
    padding: '12px 16px',
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    border: '2px solid #fca5a5',
    borderRadius: '8px',
    marginBottom: '20px',
    fontWeight: 600
  },
  success: {
    padding: '12px 16px',
    backgroundColor: '#dcfce7',
    color: '#166534',
    border: '2px solid #86efac',
    borderRadius: '8px',
    marginBottom: '20px',
    fontWeight: 600
  },
  filterBar: {
    display: 'flex',
    gap: '15px',
    marginBottom: '24px',
    flexWrap: 'wrap' as const
  },
  summaryBanner: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'space-between',
    flexWrap: 'wrap' as const,
    padding: '12px 16px',
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    marginBottom: '24px',
    fontWeight: 600,
    fontSize: '13px',
  },
  formSection: {
    marginBottom: '24px',
    paddingTop: '24px',
    borderTop: '1px solid #f0f0f0'
  },
  form: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '12px'
  },
  label: {
    display: 'block',
    marginBottom: '6px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151'
  },
  tableWrapper: {
    overflowX: 'auto' as const,
    WebkitOverflowScrolling: 'touch' as const,
    marginTop: '16px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const
  },
  headerRow: {
    background: '#fafafa'
  },
  th: {
    padding: 16,
    textAlign: 'left' as const,
    fontSize: 13,
    fontWeight: 500,
    color: 'rgba(0,0,0,0.85)',
    borderBottom: '1px solid #f0f0f0',
    whiteSpace: 'nowrap' as const
  },
  row: {
    borderBottom: '1px solid #e2e8f0',
    transition: 'background 0.2s'
  },
  td: {
    padding: '16px',
    fontSize: '14px',
    color: '#1f2937'
  }
};

export default Accounting;